import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import { errorMsg } from "../../../../../Apis/functions";
import HandleChange from '../../../../../Helpers/HandleChange';
import { CreateFineType, UpdateFineType } from '../../../../../reduxToolkit/FineTypes/FineTypesApi';
import { FINE_TYPES_MODEL, MODAL_NAMES, toggleFinesModal } from '../Model/Model';

/**
 * @Author      Jhovanny Fragoso Márquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 * 
 * Hook used to handle Add/Edit Fine Type logic.
 *
 * @hook
 * @param {boolean} show - Determines if the modal is currently visible.
 * @param {Function} onHide - Callback function to close the modal.
 * @param {Function} setModified - Function to notify parent component that data has changed.
 *
 */

const AddEditFineTypeApp = ({ show, setModified }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { fineType, setFineType } = useOutletContext();

    const [disabled, setDisabled] = useState(false);
    const [emptyFields, setEmptyFields] = useState(false);

    const { handleChange: baseHandleChange } = HandleChange({
        model: fineType,
        setModel: setFineType,
    });

    const handleChange = (e) => {
        const value = typeof e.target.value === "string"
            ? e.target.value.replace(/^\s+/, "")
            : e.target.value;
        e.target.value = value;
        baseHandleChange(e);
    };
    /**
     * Initialize fineType model when modal opens.
     */
    useEffect(() => {
        if (show) {
            setFineType(prev => prev?.id ? prev : { ...FINE_TYPES_MODEL });
            setEmptyFields(false);
        }
    }, [show]);

    /**
     * Create or update a fine type using Redux actions.
     */
    const handleSubmit = () => {
        if (!fineType.isValid()) {
            setEmptyFields(true);
            return;
        }

        const body = fineType.toAddEdit();

        dispatch(fineType?.id ? UpdateFineType(body) : CreateFineType(body))
            .then(({ payload: { data: { data, code } } }) => {
                if (data?.id) {
                    toast.success(t(fineType?.id ? 'has_been_updated_successfully' : 'has_been_created_successfully'));
                    setModified(true)
                    toggleFinesModal(MODAL_NAMES.EDIT, setFineType, false)
                    toggleFinesModal(MODAL_NAMES.CREATE, setFineType, false)
                    setFineType({ ...FINE_TYPES_MODEL });
                } else errorMsg(code);
            });
    };

    /**
     * Reset fineType model to default values.
     */
    const handleClearFields = () => {
        setFineType({ ...FINE_TYPES_MODEL });
        setEmptyFields(false);
    };


    useEffect(() => {
        if (fineType.isCreateModal) {
            setFineType((prev => ({
                ...prev,
                name: null,
                id: null
            })))
        }
    }, [fineType.isCreateModal])

    useEffect(() => {
        if (fineType.isCreateModal || fineType.isEditModal) {
            setEmptyFields(false);
        }
    }, [fineType.isCreateModal, fineType.isEditModal]);



    return {
        t,
        disabled,
        setDisabled,
        emptyFields,
        setEmptyFields,
        fineType,
        handleChange,
        handleSubmit,
        handleClearFields,
    };
};

export default AddEditFineTypeApp;
